import { html, css } from 'lit';

import '../../../packages/neb-lit-components/src/components/inputs/neb-select';
import '../controls/inputs/neb-checkbox';
import '../../../packages/neb-lit-components/src/components/inputs/neb-textarea';
import { getBillingCodesWriteOffs } from '../../../packages/neb-api-client/src/billing-codes';
import { ASSIGNED_TO_OPTIONS } from '../../../packages/neb-lit-components/src/components/forms/utils';
import { BUTTON_ROLE } from '../../../packages/neb-lit-components/src/components/neb-button';
import NebPopup from '../../../packages/neb-popup/src/neb-popup';
import { FINANCIAL_CLASSES } from '../../../packages/neb-utils/patientInsurance';
import { CSS_FONT_FAMILY, CSS_SPACING } from '../../styles';

export const ELEMENTS = {
  adjustmentCodesTextarea: {
    id: 'adjustment-codes-textarea',
  },
  issueTextarea: {
    id: 'issue-textarea',
  },
  assignedToDropdown: {
    id: 'assigned-to-dropdown',
  },
  correctedCheckbox: {
    id: 'corrected-checkbox',
  },
  buttonSave: {
    id: 'button-save',
  },
  buttonCancel: {
    id: 'button-cancel',
  },
};

class NebPopupDenyDisputeClaim extends NebPopup {
  static get properties() {
    return {
      __issueText: {
        type: String,
      },
      __assignedTo: {
        type: String,
      },
      __setAsCorrected: {
        type: Boolean,
      },
      __adjustmentCodesText: {
        type: String,
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        :host {
          width: 525px;
          font-family: ${CSS_FONT_FAMILY};
        }

        :host([layout='small']) {
          width: 350px;
        }

        .content-container {
          display: flex;
          flex-direction: column;
          flex: 1 0 0;
          justify-content: space-between;
          height: 100%;
        }

        .button-container {
          display: flex;
          justify-content: start;
          margin-top: 10px;
        }

        .spacing {
          margin-right: 10px;
        }

        .field-spacing {
          margin-bottom: ${CSS_SPACING};
        }

        .description {
          margin-bottom: 20px;
        }
      `,
    ];
  }

  initState() {
    super.initState();

    this.title = 'Needs Attention';
    this.model = {
      financialClass: '',
      adjustmentCodes: '',
    };

    this.__issueText = '';
    this.__assignedTo = { value: '', label: '' };
    this.__setAsCorrected = true;
    this.__adjustmentCodes = [];
    this.__adjustmentCodesText = '';
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      confirm: () => {
        if (!this.__assignedTo.value || !this.__issueText) return;

        const adjustmentCodes = this.model?.adjustmentCodes?.length
          ? { adjustmentCodes: this.__adjustmentCodesText }
          : {};

        this.onClose({
          issue: this.__issueText,
          assignedTo: this.__assignedTo.value,
          setAsCorrected: this.__setAsCorrected,
          ...adjustmentCodes,
        });
      },
      issueChanged: ({ value }) => {
        this.__issueText = value;
      },
      assignedToChanged: ({ value }) => {
        this.__assignedTo = value;
      },
      correctedChanged: ({ value }) => {
        this.__setAsCorrected = value;
      },
    };
  }

  async connectedCallback() {
    this.__adjustmentCodes = await getBillingCodesWriteOffs({}, true);

    super.connectedCallback();
  }

  __loadAdjustmentCodesField() {
    if (!this.model?.adjustmentCodes?.length) {
      this.__adjustmentCodesText = 'No adjustment codes';
      return;
    }

    const codeList = this.model.adjustmentCodes.reduce((acc, adjCode) => {
      const [codeGroup, code] = adjCode.split('-');
      const codeObj = this.__adjustmentCodes.find(
        c => c.codeGroup === codeGroup && c.code === code,
      );

      const description = codeObj?.description || '(No description)';
      acc.push(`${adjCode} - ${description}`);

      return acc;
    }, []);

    this.__adjustmentCodesText = codeList.join('\n');
  }

  updated(changedProps) {
    if (changedProps.has('model')) {
      this.__loadAdjustmentCodesField();
      this.__updateCorrectedCheckboxState();
    }
  }

  __updateCorrectedCheckboxState() {
    const payerType = this.model.financialClass || '';
    const excludedTypes = [
      FINANCIAL_CLASSES.Medicare,
      FINANCIAL_CLASSES.AutoInsurance,
      FINANCIAL_CLASSES.WorkersCompensation,
      FINANCIAL_CLASSES.OtherLiability,
    ];

    this.__setAsCorrected = !excludedTypes.includes(payerType);
  }

  renderContent() {
    return html`
      <div class="content-container">
        <div class="description">
          This action will void/cancel the current claim and create a new one
          under Needs Attention for further review. Adjustment codes will be
          included there. You may add any additional details about the issue
          below.
        </div>

        <neb-textarea
          id="${ELEMENTS.adjustmentCodesTextarea.id}"
          class="field-spacing"
          label="Adjustment Codes"
          .value="${this.__adjustmentCodesText}"
          ?disabled="${true}"
          rows="3"
        ></neb-textarea>

        <neb-textarea
          id="${ELEMENTS.issueTextarea.id}"
          class="field-spacing"
          label="Issue"
          helper="Required"
          .value="${this.__issueText}"
          .onChange="${this.handlers.issueChanged}"
          rows="4"
        ></neb-textarea>

        <neb-select
          id="${ELEMENTS.assignedToDropdown.id}"
          class="field-spacing"
          label="Assigned To"
          helper="Required"
          .items="${ASSIGNED_TO_OPTIONS}"
          .value="${this.__assignedTo}"
          .onChange="${this.handlers.assignedToChanged}"
        ></neb-select>

        <neb-checkbox
          id="${ELEMENTS.correctedCheckbox.id}"
          class="field-spacing"
          label="Set Claim as Corrected"
          .checked="${this.__setAsCorrected}"
          .onChange="${this.handlers.correctedChanged}"
        ></neb-checkbox>

        <div class="button-container">
          <neb-button
            id="${ELEMENTS.buttonSave.id}"
            class="spacing"
            label="Save"
            .role="${BUTTON_ROLE.CONFIRM}"
            .onClick="${this.handlers.confirm}"
            ?disabled="${!this.__assignedTo.value || !this.__issueText}"
          ></neb-button>

          <neb-button
            id="${ELEMENTS.buttonCancel.id}"
            label="Cancel"
            .role="${BUTTON_ROLE.OUTLINE}"
            .onClick="${this.handlers.cancel}"
          ></neb-button>
        </div>
      </div>
    `;
  }
}

customElements.define('neb-popup-deny-dispute-claim', NebPopupDenyDisputeClaim);
